/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import mbpLogger from '@module-logger/mbp';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import {
    arrayOf, bool, func, object, shape, string,
} from 'prop-types';
import { useQuery } from '@apollo/client';
import { makeStyles } from '@material-ui/styles';
import { Link } from 'react-router-dom';
import { useUIDSeed } from 'react-uid';
import { GRAPHQL_ENV } from '../../../../gql';
import findProductsByPartNumbers from '../gql/findProductsByPartNumbers';
import { isEmpty } from '../../../../pages/Account/utils/object';
import CustomSlider from '../Common/CustomSlider';
import ResponsiveImage from '../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        height: 'auto',
    },
    title: {
        position: 'relative',
        textAlign: 'center',
        fontFamily: 'ivypresto-text, PlayfairDisplayBold, serif',
        [theme.breakpoints.down(600)]: {
            fontSize: '1.8rem',
        },
    },
    sliderWrapper: {
        margin: '25px 0',
        '& .slick-current': {
            [theme.breakpoints.down(800)]: {
                '& .MuiButton-root': {
                    backgroundColor: theme.palette.colorPrimary,
                    color: '#FFF',
                },
            },
        },
        '& .slick-slide': {
            padding: '0 30px !important',
            [theme.breakpoints.down(800)]: {
                padding: '0 5px !important',
                '& .MuiButton-root': {
                    backgroundColor: '#FFF',
                    color: theme.palette.colorPrimary,
                },
            },
        },
        [theme.breakpoints.down(600)]: { margin: '15px 0' },
    },
    cardHeading: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        top: '0',
        left: '0',
        right: '0',
        transition: '500ms opacity ease-in',
        '& img': {
            width: '100%',
            minHeight: '100%',
            height: '100%',
            objectPosition: 'center',
            objectFit: 'cover',
        },
    },
    cardWrapper: {
        width: '340px',
        height: '100%',
        '& .MuiCard-root': {
            flex: '1',
            boxShadow: 'none !important',
            border: '1px solid #DADADA',
            height: '100%',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            '& .MuiCardContent-root': {
                flex: '1',
            },
        },
        '&.card-heading--border > .MuiCard-root': {
            border: '1px solid #DADADA',
        },
        '& h1,& h2,& h3,& h4,& h5,& h6': {
            fontFamily: 'ivypresto-text, PlayfairDisplayBold, serif',
            margin: 0,
            fontSize: '18px',
            lineHeight: '28px',
            marginBottom: '15px',
            [theme.breakpoints.down(600)]: {
                lineHeight: '22px',
                fontSize: '18px',
            },
        },
        '& p': {
            fontSize: '16px',
            lineHeight: '20px',
            marginBottom: '10px',
        },
    },
    cardContent: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        textAlign: 'center',
        height: '100%',
        '& .MuiButton-root': {
            color: theme.palette.white,
            textTransform: 'uppercase',
            padding: '12px 20px',
            fontWeight: '600',
            width: 'fit-content',
            height: '40px',
            backgroundColor: theme.palette.colorPrimary,
            border: `1px solid ${theme.palette.colorPrimary}`,
            fontSize: '18px',
            margin: '15px 0',
            [theme.breakpoints.down(600)]: {
                fontSize: '18px',
            },
            '-webkit-font-smoothing': 'antialiased',
            '&:hover': {
                backgroundColor: theme.palette.colorPrimary,
            },
            '&.Mui-disabled': {
                opacity: '25%',
            },
        },
        '& a': {
            textDecoration: 'none',
        },
    },
    startingPrice: {
        '& span': {
            fontSize: '20px',
            fontWeight: '700',
            [theme.breakpoints.down(600)]: {
                lineHeight: '22px',
                fontSize: '20px',
            },
        },
    },
}));

const OurFavoritesTemplate = ({
    item, brandName, templateData, handleTrackEvent, mapKey,
}) => {
    const classes = useStyles();

    if (typeof window === 'undefined') return null;

    const [windowInnerWidth, setWindowInnerWidth] = useState(window.innerWidth);
    const seed = useUIDSeed();

    const partNumbers = item?.our_favorites_block?.products?.map((product) => product.product_sku);
    const PRODUCT_BY_PART = findProductsByPartNumbers(brandName, partNumbers, null);
    const { loading, error, data } = useQuery(PRODUCT_BY_PART);
    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: PRODUCT_BY_PART,
            component: 'SubscriptionPage.js',
            message: 'Error loading data from Graphql',
            env: GRAPHQL_ENV,
            error,
        });
        return null;
    }

    let passportProducts = {};
    if (data) passportProducts = data?.findAvailProductSkusByPartNumbers;

    const handleResize = () => {
        setWindowInnerWidth(window.innerWidth);
    };

    const handleProductClick = (product) => {
        handleTrackEvent('Subscriptions Experience', 'Choose this Plan from Our Favorites', product?.product_sku || '');
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const responsiveImagePath = (name, path) => {
        const extension = (name || '').includes('_gif') ? '.gif' : 'x.jpg';
        return `${path + name + extension}`;
    };

    const renderProduct = (product, currItem) => {
        if (isEmpty(passportProducts) || isEmpty(currItem)) return <></>;
        const getProduct = passportProducts.find((partNumber) => partNumber?.partNumber === product?.product_sku);
        const subscriptionProducts = getProduct?.productSkus?.filter((sku) => sku?.subscriptions?.type !== null && sku?.subscriptions?.duration);
        const filterByPrice = subscriptionProducts?.sort((a, b) => a?.prices?.[0]?.value - b?.prices?.[0]?.value);
        const startingFrom = filterByPrice?.[0].prices?.[0]?.value || getProduct.productSkus.prices[0].value;
        const displaySubscription = currItem?.display_description || true;

        return (
            <Card>
                <div className={classes.cardHeading}>
                    <ResponsiveImage
                        alt={getProduct?.image?.altText || ''}
                        path={responsiveImagePath(getProduct?.image?.name, getProduct?.image?.path)}
                        params={{ desktop: 'quality=100', mobile: 'quality=100' }}
                    />
                </div>
                <CardContent>
                    <div className={classes.cardContent}>
                        <div>
                            <h5>{product.product_name}</h5>
                            {displaySubscription && <p>{product.product_description}</p>}
                        </div>
                        <div>
                            <p className={classes.startingPrice}>Starting at <span>${startingFrom}</span></p>
                            <Link
                                to={{
                                    pathname: '/subscriptions/customizable-plan/choose-item',
                                    search: '',
                                    hash: '',
                                    state: { selectedProduct: product, templateData },
                                }}
                            >
                                <Button data-testid="choose-this-plan" onClick={() => handleProductClick(product)}>Choose This Plan</Button>
                            </Link>
                        </div>

                    </div>
                </CardContent>
            </Card>
        );
    };

    return (
        <>
            {!loading && (
                <div id="our-favorites-block" key={mapKey} className={classes.container}>
                    <h1 className={classes.title}>{item?.our_favorites_block?.title || ''}</h1>
                    <CustomSlider parentClass={classes.sliderWrapper} slideWidth={windowInnerWidth > 600 ? 390 : 280} length={item?.our_favorites_block?.products?.length || 0}>
                        {item?.our_favorites_block?.products?.map((product, ind) => (
                            <div key={seed(ind)} className={classes.cardWrapper}>
                                {renderProduct(product, item?.our_favorites_block || {})}
                            </div>
                        ))}
                    </CustomSlider>
                </div>
            )}
        </>

    );
};

OurFavoritesTemplate.propTypes = {
    item: object.isRequired,
    brandName: string.isRequired,
    templateData: shape({
        seo: shape({
            breadcrumb: arrayOf(
                shape({
                    title: string.isRequired,
                    href: string.isRequired,
                }).isRequired,
            ),
            keywords: string,
            meta_description: string,
            seo_searchable: bool.isRequired,
            page_title: string,
        }).isRequired,
        category_blocks: arrayOf(
            shape({
                main_banner: shape({
                    link: shape({
                        href: string.isRequired,
                        title: string.isRequired,
                    }),
                    desktop_image: shape({
                        url: string.isRequired,
                    }),
                }),
                sub_banner: shape({
                    desktop_image: shape({
                        url: string.isRequired,
                        title: string.isRequired,
                    }),
                }),
                bottom_banners: shape({
                    title: string.isRequired,
                    banners: arrayOf({
                        link: shape({
                            href: string.isRequired,
                        }),
                        desktop_image: shape({
                            url: string.isRequired,
                            title: string.isRequired,
                        }),
                    }),
                }),
            }),
        ),
    }).isRequired,
    handleTrackEvent: func.isRequired,
    mapKey: string.isRequired,
};

export default OurFavoritesTemplate;
